import * as Api from '@ViewModels';
import { LoadingSpinner } from '@WebComponents/LoadingSpinner';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { toast } from 'sonner';
import { getFormattedPhoneNumber } from '../../../../models/UiUtils';
import { useAdminUserSession } from '../../../../models/hooks/adminStateHooks';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { AccountUserViewModel, AdminPermission } from '../../../../viewmodels/AdminViewModels';
import { Modal } from '../../../../web/components/Modal';
import { ITimezoneData, TimezonePicker } from '../../../../web/components/TimezonePicker';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { booleanToString, numberToString } from '../../../utils';
import { AccountDetailsInfoField } from '../../accountDetails/AccountDetailsInfoField';
import { BackorderAreacodesForm } from './BackorderAreacodesForm';
import { ImportContactsFromSentMessagesForm } from './ImportContactsFromSentMessagesForm';
import { ProviderPhoneNumberForm } from './ProviderPhoneNumberForm';
import { styleSheet } from './styles';

interface IProps {
	account: Api.IAccount;
	user: AccountUserViewModel;
}

enum EEnableOptions {
	Enabled = 'Enabled',
	Disabled = 'Disabled',
}

export function _UserDialogContent({ user, account }: IProps) {
	const errorMessages = useErrorMessages();
	const userSession = useAdminUserSession();
	const [isImportFromProviderModalOpen, setIsImportFromProviderModalOpen] = React.useState(false);
	const [isBackorderModalOpen, setIsBackorderModalOpen] = React.useState(false);
	const [isImportContactsFromSentMessagesOpen, setIsImportContactsFromSentMessagesOpen] = React.useState(false);
	const textingEnabled = account?.features?.texting?.enabled;
	const userJs = user.toJs();
	const stats = user.userWithStats?.stats;
	const impersonationContext: Api.IImpersonationContext = React.useMemo(
		() => ({
			account,
			user: user.toJs(),
		}),
		[account, user]
	);
	const userPreferences = React.useMemo(() => {
		if (!user.userWithStats?.userPreferences) {
			return null;
		}
		return user.userWithStats.userPreferences;
	}, [user.userWithStats.userPreferences]);

	React.useEffect(() => {
		const loadUserStats = async () => {
			try {
				user.loadUserStats();
			} catch (error) {
				errorMessages.pushApiError(error);
			}
		};
		loadUserStats();
	}, [user, errorMessages]);
	React.useEffect(() => {
		const loadUserPhone = async () => {
			try {
				await user.getUserPhone();
			} catch (error) {
				errorMessages.pushApiError(error);
			}
		};
		if (textingEnabled) {
			loadUserPhone();
		}
	}, [textingEnabled, user, errorMessages]);
	const onRoleChanged = (e: React.ChangeEvent<HTMLSelectElement>) => {
		user.updateRole(e.target.value as Api.UserRole)?.catch((err: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(err);
		});
	};
	const updatePreferences = (prefs: object) => {
		if (!user?.preferences) {
			return;
		}
		const calc: Api.IUserPreferences = { ...user.preferences, ...prefs };

		user.updateUserPreferencesAsAdmin(calc)?.catch((err: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(err);
		});
	};
	const onDigestNotificationIntervalChanged = (ev: React.ChangeEvent<HTMLSelectElement>) => {
		updatePreferences({ digestNotificationInterval: ev.target.value });
	};

	const onDefaultGroupChanged = (ev: React.ChangeEvent<HTMLSelectElement>) => {
		updatePreferences({ defaultGroup: ev.target.value });
	};

	const onDisableOneWayContactSyncChanged = (ev: React.ChangeEvent<HTMLSelectElement>) => {
		updatePreferences({
			disableOneWayContactSync: ev.target.value === 'Disabled',
		});
	};

	const onUpdateTimezone = (tz: ITimezoneData) => {
		updatePreferences({
			timeZone: tz.name,
		});
	};

	const onCreateContactsFromSentMessagesChanged = (ev: React.ChangeEvent<HTMLSelectElement>) => {
		updatePreferences({
			createContactsFromSentMessages: ev.target.value === 'Enabled',
		});
	};

	const onDisableCalendarContactSyncChanged = (ev: React.ChangeEvent<HTMLSelectElement>) => {
		updatePreferences({
			disableCalendarContactSync: ev.target.value === 'Disabled',
		});
	};

	const onTrackingPixelPollingChanged = (ev: React.ChangeEvent<HTMLSelectElement>) => {
		if (!user?.preferences) {
			return;
		}
		const calc: Api.IUserPreferences = { ...user.preferences };

		user
			.updateTrackingPixelPollingValue(calc, ev.target.value === 'Enabled')
			?.catch((err: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(err);
			});
	};

	const onEmailConnectionOptOutChanged = (ev: React.ChangeEvent<HTMLSelectElement>) => {
		// The value of `'Yes' === false` and `'No' === true`
		const optOut = ev.target.value === 'No';
		user.updateEmailConnectionOptOutValue(optOut).catch((err: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(err);
		});
	};

	const renderLevTextingNumber = () => {
		if (
			!user.textingPhoneNumber?.number &&
			userSession.hasPermission(AdminPermission.SupportLevel3) &&
			Boolean(account?.features?.texting?.enabled)
		) {
			const label = `Import from ${account.features.texting.provider}`;
			return (
				<button className={css(baseStyleSheet.brandLink)} onClick={() => setIsImportFromProviderModalOpen(true)}>
					{label}
				</button>
			);
		}
		if (user.textingPhoneNumber?.number) {
			const number = `${user.textingPhoneNumber.number} (${user.textingPhoneNumber.provider})`;
			return <p className={css(styleSheet.phoneNumber)}>{number}</p>;
		}
		return <>Not Available</>;
	};

	const onProviderPhoneNumberSaved = () => {
		toast.success('Phone number imported.');
		setIsImportFromProviderModalOpen(false);
	};

	const onBackordersSaved = () => {
		toast.success(`Back order has been submitted. Support and the CSM will be notified when it's fulfilled`);
		setIsBackorderModalOpen(false);
	};

	const onImportInitiated = () => {
		toast.success(`Import has started successfully.`);

		// Implicitly set the preference to true. The platform will set to true if it's false already.
		user.preferences.createContactsFromSentMessages = true;
		setIsImportContactsFromSentMessagesOpen(false);
	};

	return (
		<section>
			<h3 className={css(baseStyleSheet.truncateText, styleSheet.title)} title={user.name || undefined}>
				{user.name}
			</h3>
			{userJs.lastTestEmail ? (
				<div className={css(styleSheet.container)}>
					<div className={css(styleSheet.sectionTitle)}>Test Email</div>
					<div className={css(styleSheet.section)}>
						<div className={css(styleSheet.sectionColumn)}>
							<AccountDetailsInfoField label='Display Name'>
								{userJs.lastTestEmail.displayName || 'Not available'}
							</AccountDetailsInfoField>
						</div>
						<div className={css(styleSheet.sectionColumn)}>
							<AccountDetailsInfoField label='Sent On'>
								{moment(userJs.lastTestEmail.creationDate).format('MM-DD-YYYY HH:MM:SS A') || 'Not available'}
							</AccountDetailsInfoField>
						</div>
						<div className={css(styleSheet.sectionColumn)}>
							<AccountDetailsInfoField label='Sending As'>
								{userJs.lastTestEmail.emailAddress || 'Not available'}
							</AccountDetailsInfoField>
						</div>
					</div>
				</div>
			) : null}
			<div className={css(styleSheet.container)}>
				<div className={css(styleSheet.sectionTitle)}>Key Stats</div>
				<div className={css(styleSheet.section)}>
					<div className={css(styleSheet.sectionColumn)}>
						<AccountDetailsInfoField label='Connected Email'>
							{booleanToString(stats?.emailCalendarTokenExists)}
						</AccountDetailsInfoField>
						<AccountDetailsInfoField label='Imported Contacts'>
							{booleanToString(stats?.contactsImported)}
						</AccountDetailsInfoField>
						<AccountDetailsInfoField label='Contact Import Date'>
							{stats?.contactImportDate ? moment(stats.contactImportDate).format('MM-DD-YYYY') : 'Not available'}
						</AccountDetailsInfoField>
						<AccountDetailsInfoField label='Webhook Status'>
							{stats?.webhookLastMessageReceivedOn ? 'Connected' : 'Not available'}
						</AccountDetailsInfoField>
						{!!stats?.webhookLastMessageReceivedOn && (
							<AccountDetailsInfoField label='Last Webhook Message'>
								{moment(stats.webhookLastMessageReceivedOn).format('MM-DD-YYYY')}
							</AccountDetailsInfoField>
						)}
					</div>
					<div className={css(styleSheet.sectionColumn)}>
						<AccountDetailsInfoField label='Installed Email Add-In'>
							{booleanToString(user?.userMilestones?.hasUsedEmailAddIn)}
						</AccountDetailsInfoField>
						<AccountDetailsInfoField label='Installed Mobile App'>
							{booleanToString(user?.userMilestones?.hasUsedMobileApp)}
						</AccountDetailsInfoField>
						<AccountDetailsInfoField label='Contacts Created'>
							{numberToString(stats?.contactsCreated)}
						</AccountDetailsInfoField>
					</div>
					<div className={css(styleSheet.sectionColumn)}>
						{userSession.hasPermission(AdminPermission.ModifyAccount) ? (
							<AccountDetailsInfoField label='Import Contacts from Sent Emails'>
								<button
									className={css(baseStyleSheet.brandLink)}
									onClick={() => setIsImportContactsFromSentMessagesOpen(true)}
								>
									Start Import
								</button>
							</AccountDetailsInfoField>
						) : null}
					</div>
					<div className={css(styleSheet.sectionColumn)}>
						<AccountDetailsInfoField label='Most Recent Note Creation'>
							{stats?.mostRecentNoteCreationDate
								? moment(stats.mostRecentNoteCreationDate).format('MM-DD-YYYY')
								: 'Not available'}
						</AccountDetailsInfoField>
						{userJs && (
							<AccountDetailsInfoField label='Mobile Phone'>
								{userJs.mobilePhone ? getFormattedPhoneNumber(userJs.mobilePhone) : 'Not available'}
							</AccountDetailsInfoField>
						)}
						<AccountDetailsInfoField label='Levitate Texting Number'>
							{renderLevTextingNumber()}
						</AccountDetailsInfoField>
						{user.textingPhoneNumber?.number && (
							<AccountDetailsInfoField label='Call Forwarding for Text Number'>
								{user.textingPhoneNumber?.callForwardingNumber?.phoneNumber?.standard || 'Not assigned'}
							</AccountDetailsInfoField>
						)}
						{!user.textingPhoneNumber?.number && userSession.hasPermission(AdminPermission.ModifyAccount) ? (
							<AccountDetailsInfoField label='Backorder Texting Number'>
								<button className={css(baseStyleSheet.brandLink)} onClick={() => setIsBackorderModalOpen(true)}>
									Open request form
								</button>
							</AccountDetailsInfoField>
						) : null}
					</div>
				</div>
				<div className={css(styleSheet.sectionTitle)}>Preferences</div>
				<div className={css(styleSheet.section)} style={{ position: 'relative' }}>
					<div className={css(styleSheet.sectionColumn)}>
						<AccountDetailsInfoField label='Role'>
							<select
								className={css(styleSheet.dropDown)}
								disabled={user.isBusy || !userSession.hasPermission(AdminPermission.ModifyUser)}
								value={user.role}
								onChange={onRoleChanged}
							>
								<option value='limitedUser'>Limited User</option>
								<option value='user'>User</option>
								<option value='admin'>Admin</option>
								<option value='superAdmin'>Super Admin</option>
							</select>
						</AccountDetailsInfoField>
						<AccountDetailsInfoField label='Digest Notification Schedule'>
							<select
								className={css(styleSheet.dropDown)}
								disabled={user.isBusy || !userSession.hasPermission(AdminPermission.ModifyUser)}
								value={user.preferences.digestNotificationInterval}
								onChange={onDigestNotificationIntervalChanged}
							>
								{['Daily', 'Weekly', 'Never'].map(x => (
									<option value={x} key={`value-${x}`}>
										{x}
									</option>
								))}
							</select>
						</AccountDetailsInfoField>

						<AccountDetailsInfoField label='Timezone'>
							<TimezonePicker
								inputStyles={[styleSheet.dropDown]}
								onTimezoneSelected={onUpdateTimezone}
								initialTimezoneValue={userPreferences?.timeZone}
								disabled={user.isBusy || !userSession.hasPermission(AdminPermission.ModifyUser)}
							/>
						</AccountDetailsInfoField>
					</div>
					<div className={css(styleSheet.sectionColumn)}>
						<AccountDetailsInfoField
							label='Default Visibility'
							info='This setting will be used as the default visibility for notes and contacts.'
						>
							<select
								className={css(styleSheet.dropDown)}
								disabled={user.isBusy || !userSession.hasPermission(AdminPermission.ModifyUser)}
								value={user.preferences.defaultGroup}
								onChange={onDefaultGroupChanged}
							>
								<option value='admin'>Private</option>
								<option value='all'>Public</option>
							</select>
						</AccountDetailsInfoField>
						<AccountDetailsInfoField label='One Way Contacts Sync'>
							<select
								className={css(styleSheet.dropDown)}
								disabled={user.isBusy || !userSession.hasPermission(AdminPermission.ModifyUser)}
								value={!user.preferences.disableOneWayContactSync ? 'Enabled' : 'Disabled'}
								onChange={onDisableOneWayContactSyncChanged}
							>
								{['Enabled', 'Disabled'].map(x => (
									<option value={x} key={`value-${x}`}>
										{x}
									</option>
								))}
							</select>
						</AccountDetailsInfoField>
					</div>
					<div className={css(styleSheet.sectionColumn)}>
						<AccountDetailsInfoField label='Create Contacts from Sent Emails'>
							<select
								className={css(styleSheet.dropDown)}
								disabled={user.isBusy || !userSession.hasPermission(AdminPermission.ModifyUser)}
								onChange={onCreateContactsFromSentMessagesChanged}
								value={user.preferences.createContactsFromSentMessages ? 'Enabled' : 'Disabled'}
							>
								{Object.values(EEnableOptions).map(option => (
									<option value={option} key={`createcontact-value-${option}`}>
										{option}
									</option>
								))}
							</select>
						</AccountDetailsInfoField>
						<AccountDetailsInfoField label='Calendar Contact Sync'>
							<select
								className={css(styleSheet.dropDown)}
								disabled={user.isBusy || !userSession.hasPermission(AdminPermission.ModifyUser)}
								onChange={onDisableCalendarContactSyncChanged}
								value={user.preferences.disableCalendarContactSync ? 'Disabled' : 'Enabled'}
							>
								{Object.values(EEnableOptions).map(option => (
									<option value={option} key={`calendarcontact-value-${option}`}>
										{option}
									</option>
								))}
							</select>
						</AccountDetailsInfoField>
					</div>

					<div className={css(styleSheet.sectionColumn)}>
						<AccountDetailsInfoField label='Tracking Pixel Polling'>
							<select
								className={css(styleSheet.dropDown)}
								disabled={user.isBusy || !userSession.hasPermission(AdminPermission.ModifyUser)}
								onChange={onTrackingPixelPollingChanged}
								value={user.preferences.clients?.outlookClient?.trackingPixelPollingEnabled ? 'Enabled' : 'Disabled'}
							>
								{Object.values(EEnableOptions).map(option => (
									<option value={option} key={`trackingpixel-value-${option}`}>
										{option}
									</option>
								))}
							</select>
						</AccountDetailsInfoField>

						<AccountDetailsInfoField label='Require Email Connection'>
							<select
								className={css(styleSheet.dropDown)}
								disabled={user.isBusy || !userSession.hasPermission(AdminPermission.ModifyUser)}
								value={user.preferences.emailConnectionOptOut === false ? 'Yes' : 'No'}
								onChange={onEmailConnectionOptOutChanged}
							>
								<option value='Yes' key='emailConnectionOptOut-value-yes'>
									Yes
								</option>
								<option value='No' key='emailConnectionOptOut-value-no'>
									No
								</option>
							</select>
						</AccountDetailsInfoField>
					</div>

					{!!user.isBusy && <LoadingSpinner className={css(baseStyleSheet.absoluteCenter)} type='large' />}
				</div>
			</div>
			<Modal
				isOpen={isImportFromProviderModalOpen}
				onRequestClose={() => setIsImportFromProviderModalOpen(false)}
				useDefaultHeader={true}
			>
				<ProviderPhoneNumberForm
					impersonationContext={impersonationContext}
					onSuccess={onProviderPhoneNumberSaved}
					provider={account.features.texting.provider}
				/>
			</Modal>
			<Modal
				isOpen={isBackorderModalOpen}
				onRequestClose={() => setIsBackorderModalOpen(false)}
				useDefaultHeader={true}
			>
				<BackorderAreacodesForm onSuccess={onBackordersSaved} impersonationContext={impersonationContext} />
			</Modal>
			<Modal
				isOpen={isImportContactsFromSentMessagesOpen}
				onRequestClose={() => setIsImportContactsFromSentMessagesOpen(false)}
				useDefaultHeader={true}
			>
				<ImportContactsFromSentMessagesForm onSuccess={onImportInitiated} impersonationContext={impersonationContext} />
			</Modal>
		</section>
	);
}

export const UserDialogContent = observer(_UserDialogContent);
